import { useEffect, useState, type ReactNode } from 'react';

/** the longest closing motion (--dur-spatial and a frame); the content goes at the latest after it */
const CLOSE_MS = 400;

/**
 * Content that a switch or a choice shows or hides (the standby rows, a grind tip): it opens and closes over its height
 * instead of pushing everything below in one frame (22.10.2026). The height runs on grid rows (0fr ↔ 1fr), so no
 * height is measured; while closing the last content stays until the motion ends, then it is gone from the page.
 * Shown from the start (first render), it appears without motion.
 */
export function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  /** in the page (also while closing) */
  const [mounted, setMounted] = useState(open);
  /** at full height; set two frames after mounting, so the closed state is painted first and the opening runs */
  const [shown, setShown] = useState(open);
  // the content of the last open render stays while it closes (the parent already passes nothing then)
  const [kept, setKept] = useState<ReactNode>(children);
  if (open && kept !== children) setKept(children);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => setShown(true));
      });
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!open && !mounted) return null;
  return (
    <div className="collapse" data-open={(open && shown) || undefined}>
      <div className="collapse-inner">{open ? children : kept}</div>
    </div>
  );
}
